import { addDays, BodyweightList, convertWeight, IsoDate, OkResponse, todayIn, type BodyweightEntry, type User } from '@squats/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { z } from 'zod';
import { api, errorText } from '../api.ts';
import { useUser } from '../auth.tsx';
import { useCachedApi } from '../cache.ts';
import { TrendChart, type Point } from '../components/TrendChart.tsx';
import { formatDayShort } from '../dates.ts';

const RANGES = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: 'Year', days: 365 },
  { label: 'All', days: null },
] as const;

/** In the person's units, to one decimal. */
const shown = (e: BodyweightEntry, units: User['units']) => Math.round(convertWeight(e.weight, e.unit, units) * 10) / 10;

/** Average of the entries in the 7 days up to each date: smooths out day-to-day water swings. */
function weeklyAverage(points: Point[]): Point[] {
  return points.map((p) => {
    const from = addDays(p.date, -6);
    const window = points.filter((q) => q.date >= from && q.date <= p.date);
    return { date: p.date, value: window.reduce((sum, q) => sum + q.value, 0) / window.length };
  });
}

/** Your bodyweight log. Private: nobody else in the group can see it. */
export function BodyweightPage() {
  const user = useUser();
  const today = todayIn(user.timezone);
  const { data, error, reload } = useCachedApi(user.id, '/api/bodyweight', BodyweightList);
  const [range, setRange] = useState<number | null>(90);
  const entries = useMemo(() => data?.entries ?? [], [data]);
  const todays = entries.find((e) => e.date === today);
  const latest = entries.at(-1);

  const points = useMemo(
    () => entries.filter((e) => range === null || e.date >= addDays(today, -range)).map((e) => ({ date: e.date, value: shown(e, user.units) })),
    [entries, range, today, user.units],
  );
  const monthAgo = [...entries].reverse().find((e) => e.date <= addDays(today, -28));
  const change = latest && monthAgo ? shown(latest, user.units) - shown(monthAgo, user.units) : null;

  return (
    <div className="stack-lg">
      <div className="stack-sm">
        <Link href="/" className="small">
          ‹ Today
        </Link>
        <h1>Bodyweight</h1>
        <p className="muted small">Only you can see this.</p>
      </div>

      {/* Seeded once the saved entries load; after that it keeps what was typed and its "Saved" note. */}
      <LogForm key={data ? 'ready' : 'loading'} user={user} today={today} initial={todays ?? latest} onSaved={reload} />

      {error && !data && <p className="error">{error}</p>}

      {entries.length > 0 && (
        <section className="card stack" aria-labelledby="trend-h">
          <h2 id="trend-h">Trend</h2>
          <span className="chips" role="group" aria-label="Time range">
            {RANGES.map((r) => (
              <button key={r.label} type="button" className={`chip toggle${range === r.days ? ' on' : ''}`} aria-pressed={range === r.days} onClick={() => setRange(r.days)}>
                {r.label}
              </button>
            ))}
          </span>
          {latest && (
            <p>
              <strong>
                {shown(latest, user.units)} {user.units}
              </strong>{' '}
              <span className="muted small">on {formatDayShort(latest.date)}</span>
              {change !== null && (
                <span className="muted small">
                  {' '}
                  · {change > 0 ? '+' : change < 0 ? '−' : ''}
                  {Math.abs(change).toFixed(1)} {user.units} in the last month
                </span>
              )}
            </p>
          )}
          {points.length > 1 ? (
            <>
              <TrendChart points={points} smooth={weeklyAverage(points)} label="Bodyweight" format={(v) => `${v.toFixed(1)}`} />
              <p className="muted small center">Dots are daily entries; the line is the 7-day average.</p>
            </>
          ) : (
            <p className="muted small">Log a few more days to see a trend.</p>
          )}
        </section>
      )}

      {entries.length > 0 && <EntryList user={user} entries={entries} onChanged={reload} />}
    </div>
  );
}

function LogForm({ user, today, initial, onSaved }: { user: User; today: string; initial: BodyweightEntry | undefined; onSaved: () => void }) {
  const [date, setDate] = useState(today);
  const [weight, setWeight] = useState(initial ? String(shown(initial, user.units)) : '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const value = Number(weight);
  const valid = weight.trim() !== '' && Number.isFinite(value) && value >= 20 && value <= 1500 && IsoDate.safeParse(date).success && date <= today;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setMessage(null);
    try {
      await api(`/api/bodyweight/${date}`, z.object({ entry: z.unknown() }), { method: 'PUT', body: { weight: value, unit: user.units } });
      setMessage(`Saved for ${date === today ? 'today' : formatDayShort(date)}.`);
      onSaved();
    } catch (err) {
      setMessage(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card stack" onSubmit={save} aria-label="Log bodyweight">
      <div className="row wrap">
        <label className="field grow">
          <span>Weight ({user.units})</span>
          <input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value.replace(',', '.'))} onFocus={(e) => e.target.select()} placeholder="e.g. 180.4" />
        </label>
        <label className="field">
          <span>Date</span>
          <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <button type="submit" className="btn primary" disabled={!valid || busy}>
        Save
      </button>
      {message && (
        <p className="muted small" role="status">
          {message}
        </p>
      )}
      <p className="muted small">Weighing at the same time each day (say, mornings) makes the trend more useful. Logging a day again replaces it.</p>
    </form>
  );
}

function EntryList({ user, entries, onChanged }: { user: User; entries: BodyweightEntry[]; onChanged: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const newest = [...entries].reverse();
  const remove = async (date: string) => {
    setError(null);
    try {
      await api(`/api/bodyweight/${date}`, OkResponse, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <section className="card stack" aria-labelledby="entries-h">
      <h2 id="entries-h">Entries</h2>
      {error && <p className="error">{error}</p>}
      <ul className="list">
        {(showAll ? newest : newest.slice(0, 14)).map((e) => (
          <li key={e.date} className="list-row compact">
            <span className="grow">{formatDayShort(e.date)}</span>
            <span>
              {shown(e, user.units)} {user.units}
            </span>
            <button type="button" className="btn small" onClick={() => void remove(e.date)} aria-label={`Delete ${formatDayShort(e.date)}`}>
              ✕
            </button>
          </li>
        ))}
      </ul>
      {newest.length > 14 && !showAll && (
        <button type="button" className="btn small" onClick={() => setShowAll(true)}>
          Show all {newest.length}
        </button>
      )}
    </section>
  );
}
